import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PWAInstallPrompt from '../components/PWAInstallPrompt';
import axios from 'axios';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Calendar as CalendarIcon, Clock, User, Globe } from 'lucide-react';
import dayjs from 'dayjs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ScheduleMeeting = () => {
  const { hostId } = useParams();
  const navigate = useNavigate();
  const [host, setHost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);
  const [clientTz] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [meetingDuration, setMeetingDuration] = useState(30);
  const [formData, setFormData] = useState({
    clientName: '',
    clientEmail: '',
    notes: ''
  });
  const [scheduling, setScheduling] = useState(false);

  useEffect(() => {
    loadHostAvailability();
  }, [hostId]);

  useEffect(() => {
    if (selectedDate) {
      filterTimesForDate(selectedDate);
    }
  }, [selectedDate]);

  const loadHostAvailability = async () => {
    try {
      const [hostRes, availRes] = await Promise.all([
        axios.get(`${API}/users/${hostId}`),
        axios.get(`${API}/meetings/${hostId}/availability?client_tz=${clientTz}`)
      ]);
      
      setHost(hostRes.data);
      setAvailableTimes(availRes.data.availableTimes);
      setMeetingDuration(availRes.data.meetingDuration || 30);
    } catch (error) {
      toast.error('Failed to load availability');
    } finally {
      setLoading(false);
    }
  };

  const filterTimesForDate = (date) => {
    const dateStr = dayjs(date).format('YYYY-MM-DD');
    const filtered = availableTimes.filter(time => time.date === dateStr);
    return filtered;
  };

  const tileDisabled = ({ date }) => {
    const dateStr = dayjs(date).format('YYYY-MM-DD');
    return !availableTimes.some(time => time.date === dateStr);
  };

  const handleSchedule = async () => {
    if (!selectedTime || !formData.clientName || !formData.clientEmail) {
      toast.error('Please fill all required fields');
      return;
    }

    setScheduling(true);
    try {
      const response = await axios.post(`${API}/meetings/schedule`, {
        hostId,
        clientName: formData.clientName,
        clientEmail: formData.clientEmail,
        clientTz,
        selectedDateTime: selectedTime.slot_datetime,
        duration: meetingDuration,
        notes: formData.notes
      });

      toast.success('Meeting scheduled successfully!');
      navigate(`/meeting/confirmed/${response.data.meetingId}`);
    } catch (error) {
      toast.error('Failed to schedule meeting');
    } finally {
      setScheduling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-600">Loading availability...</p>
      </div>
    );
  }

  const timesForSelectedDate = selectedDate ? filterTimesForDate(selectedDate) : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 py-8">
      {/* PWA Install Prompt */}
      <PWAInstallPrompt
        appName="Digi Schedule"
        appIcon="📅"
        appColor="#3b82f6"
        storageKey="digischedule-install-prompt"
      />
      
      <div className="max-w-6xl mx-auto px-4">
        <Button
          variant="ghost"
          onClick={() => navigate(`/profile/${hostId}`)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Profile
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Calendar & Time Selection */}
          <div className="space-y-6">
            <Card className="p-6 bg-white shadow-lg">
              <div className="flex items-center gap-4 mb-6 pb-6 border-b border-slate-200">
                {host?.profilePicUrl ? (
                  <img src={host.profilePicUrl} className="w-16 h-16 rounded-full" alt={host.name} />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-slate-200 flex items-center justify-center">
                    <User className="w-8 h-8 text-slate-500" />
                  </div>
                )}
                <div>
                  <h1 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                    Schedule Meeting
                  </h1>
                  <p className="text-slate-600">with {host?.name}</p>
                </div>
              </div>

              {/* Timezone Info */}
              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Globe className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-semibold text-blue-900">Timezone Information</span>
                </div>
                <p className="text-sm text-blue-800">Times shown in your timezone: {clientTz}</p>
              </div>

              {/* Calendar */}
              <div className="mb-6">
                <Label className="block mb-3 font-semibold">Select Date</Label>
                <div className="calendar-container">
                  <Calendar
                    onChange={setSelectedDate}
                    value={selectedDate}
                    minDate={new Date()}
                    tileDisabled={tileDisabled}
                    className="border-0 w-full"
                  />
                </div>
              </div>

              {/* Available Times */}
              {selectedDate && (
                <div>
                  <Label className="block mb-3 font-semibold">Available Times</Label>
                  {timesForSelectedDate.length === 0 ? (
                    <p className="text-sm text-slate-600">No available times for this date</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {timesForSelectedDate.map((time, idx) => (
                        <Button
                          key={idx}
                          variant={selectedTime?.slot_datetime === time.slot_datetime ? 'default' : 'outline'}
                          onClick={() => setSelectedTime(time)}
                          className="flex flex-col items-start p-3 h-auto"
                        >
                          <span className="font-semibold">{time.client_time}</span>
                          <span className="text-xs opacity-70">Host: {time.user_time}</span>
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>
          </div>

          {/* Right: Booking Form */}
          <div>
            <Card className="p-6 bg-white shadow-lg sticky top-6">
              <h2 className="text-xl font-bold text-slate-900 mb-6">Your Information</h2>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="clientName">Name *</Label>
                  <Input
                    id="clientName"
                    value={formData.clientName}
                    onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                    placeholder="Your full name"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="clientEmail">Email *</Label>
                  <Input
                    id="clientEmail"
                    type="email"
                    value={formData.clientEmail}
                    onChange={(e) => setFormData({ ...formData, clientEmail: e.target.value })}
                    placeholder="your@email.com"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Any specific topics or questions?"
                    rows={3}
                  />
                </div>

                {/* Summary */}
                {selectedTime && (
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-sm font-semibold text-slate-900 mb-2">Meeting Summary</p>
                    <div className="text-sm text-slate-700 space-y-1">
                      <p><strong>Date:</strong> {dayjs(selectedDate).format('MMMM D, YYYY')}</p>
                      <p><strong>Time:</strong> {selectedTime.client_time}</p>
                      <p><strong>Duration:</strong> {meetingDuration} minutes</p>
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleSchedule}
                  disabled={!selectedTime || scheduling}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white py-6 text-lg rounded-full mt-6"
                >
                  <CalendarIcon className="w-5 h-5 mr-2" />
                  {scheduling ? 'Scheduling...' : 'Schedule Meeting'}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>

      <style>{`
        .calendar-container .react-calendar {
          width: 100%;
          border: none;
          font-family: inherit;
        }
        .react-calendar__tile--active {
          background: #1e293b !important;
          color: white !important;
        }
        .react-calendar__tile:disabled {
          background-color: #f1f5f9;
          color: #cbd5e1;
        }
        .react-calendar__tile:enabled:hover {
          background-color: #e2e8f0;
        }
      `}</style>
    </div>
  );
};

export default ScheduleMeeting;
